import React, { useState, useEffect, useRef } from 'react'
import { MapElement } from '../../../types'
import {
  PanelContainer,
  PanelHeader,
  PanelTitle,
  ElementList,
  ElementItem,
  ElementIcon,
  ElementName,
  ElementInfo,
  SearchInput,
  CategoryHeader,
  CategorySection,
  CollapseIcon,
  EmptyState,
  VisibilityToggle,
} from './styled'

interface NavigatorPanelProps {
  elements: MapElement[]
  selectedElement: MapElement | null
  hoveredElementId: string | null
  isolatedElementId: string | null
  onSelectElement: (element: MapElement | null) => void
  onHoverElement: (elementId: string | null) => void
  onToggleVisibility?: (elementId: string) => void
  onToggleIsolation?: (elementId: string) => void
}

const getElementIcon = (type: string): string => {
  const icons: { [key: string]: string } = {
    wall: '━',
    door: '🚪',
    window: '⬜',
    room: '▢',
    stairs: '▤',
    text: 'T',
    poi: '📍',
    polygon: '⬟',
    rectangle: '▭',
    circle: '○',
    line: '╱',
    measurement: '📏',
    'fixture-rect': '▨',
    'fixture-circle': '◉',
    'fixture-polygon': '⬢',
    'fixture-line': '═',
    'fixture-point': '•',
  }
  return icons[type] || '•'
}

const getElementDisplayName = (element: MapElement): { name: string; label?: string } => {
  // Use the most specific label available
  // For rooms, prioritize roomName over generic label
  let label: string | undefined

  if (element.type === 'room' && element.properties.roomName) {
    label = element.properties.roomName
  } else {
    label = element.properties.label ||
            element.properties.text ||
            element.properties.name ||
            element.properties.roomName
  }

  // Generate a default name based on type
  const typeNames: { [key: string]: string } = {
    wall: 'Wall',
    door: 'Door',
    window: 'Window',
    room: 'Room',
    stairs: 'Stairs',
    text: 'Text',
    poi: 'Point of Interest',
    polygon: 'Polygon',
    rectangle: 'Rectangle',
    circle: 'Circle',
    line: 'Line',
    measurement: 'Measurement',
    'fixture-rect': 'Fixture Rectangle',
    'fixture-circle': 'Fixture Circle',
    'fixture-polygon': 'Fixture Polygon',
    'fixture-line': 'Fixture Line',
    'fixture-point': 'Fixture Point',
  }

  const typeName = typeNames[element.type] || element.type

  if (label) {
    // If we have a label, use it as the name with type in parentheses
    return {
      name: label,
      label: typeName
    }
  } else {
    // Otherwise use the type name with ID
    return {
      name: `${typeName} ${element.id.slice(-4)}`,
      label: undefined
    }
  }
}

const NavigatorPanel: React.FC<NavigatorPanelProps> = ({
  elements,
  selectedElement,
  hoveredElementId,
  isolatedElementId,
  onSelectElement,
  onHoverElement,
  onToggleVisibility,
  onToggleIsolation,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set())
  const elementListRef = useRef<HTMLDivElement>(null)
  const selectedElementRef = useRef<HTMLDivElement>(null)
  const previousSelectedIdRef = useRef<string | null>(null)

  // Group elements by category
  const categories = {
    'Structural': ['wall', 'door', 'window', 'stairs'],
    'Spaces': ['room', 'polygon'],
    'Fixtures': ['fixture-rect', 'fixture-circle', 'fixture-polygon', 'fixture-line', 'fixture-point'],
    'Annotations': ['text', 'poi', 'measurement'],
    'Shapes': ['rectangle', 'circle', 'line'],
  }

  // Filter elements based on search - search through multiple attributes
  const filteredElements = elements.filter(element => {
    const searchLower = searchTerm.toLowerCase()
    const { name } = getElementDisplayName(element)

    // Search in display name
    if (name.toLowerCase().includes(searchLower)) return true

    // Search in type
    if (element.type.toLowerCase().includes(searchLower)) return true

    // Search in all property values
    const propertyValues = Object.values(element.properties)
      .filter(val => typeof val === 'string')
      .map(val => (val as string).toLowerCase())

    return propertyValues.some(val => val.includes(searchLower))
  })

  // Group filtered elements by category
  const groupedElements: { [key: string]: MapElement[] } = {}
  Object.entries(categories).forEach(([category, types]) => {
    const categoryElements = filteredElements.filter(el => types.includes(el.type))
    if (categoryElements.length > 0) {
      groupedElements[category] = categoryElements
    }
  })

  // Handle uncategorized elements
  const categorizedTypes = Object.values(categories).flat()
  const uncategorized = filteredElements.filter(el => !categorizedTypes.includes(el.type))
  if (uncategorized.length > 0) {
    groupedElements['Other'] = uncategorized
  }

  const toggleCategory = (category: string) => {
    const newCollapsed = new Set(collapsedCategories)
    if (newCollapsed.has(category)) {
      newCollapsed.delete(category)
    } else {
      newCollapsed.add(category)
    }
    setCollapsedCategories(newCollapsed)
  }

  // Auto-scroll to selected element only on new selection
  useEffect(() => {
    // Only scroll if this is a new selection (not just a re-render)
    if (selectedElement &&
        selectedElement.id !== previousSelectedIdRef.current &&
        selectedElementRef.current &&
        elementListRef.current) {

      // Update the previous selected ID
      previousSelectedIdRef.current = selectedElement.id

      // Check if element is in a collapsed category and expand it
      const elementCategory = Object.entries(categories).find(([_, types]) =>
        types.includes(selectedElement.type)
      )?.[0]

      if (elementCategory && collapsedCategories.has(elementCategory)) {
        setCollapsedCategories(prev => {
          const newSet = new Set(prev)
          newSet.delete(elementCategory)
          return newSet
        })
      }

      // Scroll to the element with a small delay to allow for category expansion
      setTimeout(() => {
        if (selectedElementRef.current && elementListRef.current) {
          const listRect = elementListRef.current.getBoundingClientRect()
          const elementRect = selectedElementRef.current.getBoundingClientRect()

          // Check if element is outside visible area
          if (elementRect.top < listRect.top || elementRect.bottom > listRect.bottom) {
            selectedElementRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            })
          }
        }
      }, 100)
    } else if (!selectedElement) {
      // Clear the previous selected ID if nothing is selected
      previousSelectedIdRef.current = null
    }
  }, [selectedElement?.id, categories, collapsedCategories])

  return (
    <PanelContainer>
      <PanelHeader>
        <PanelTitle>
          Navigator
          {isolatedElementId && (
            <ElementInfo style={{ marginLeft: '8px', color: '#007bff' }}>
              (Isolation Mode)
            </ElementInfo>
          )}
        </PanelTitle>
      </PanelHeader>

      <SearchInput
        type="text"
        placeholder="Search elements..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <ElementList ref={elementListRef}>
        {Object.keys(groupedElements).length === 0 ? (
          <EmptyState>
            {searchTerm ? 'No elements match your search' : 'No elements on canvas'}
          </EmptyState>
        ) : (
          Object.entries(groupedElements).map(([category, categoryElements]) => (
            <CategorySection key={category}>
              <CategoryHeader
                onClick={() => toggleCategory(category)}
                isCollapsed={collapsedCategories.has(category)}
              >
                <span>{category} ({categoryElements.length})</span>
                <CollapseIcon isCollapsed={collapsedCategories.has(category)}>
                  ▼
                </CollapseIcon>
              </CategoryHeader>

              {!collapsedCategories.has(category) && (
                <>
                  {categoryElements.map((element) => (
                    <ElementItem
                      key={element.id}
                      ref={selectedElement?.id === element.id ? selectedElementRef : null}
                      isSelected={selectedElement?.id === element.id}
                      isHovered={hoveredElementId === element.id}
                      style={{
                        opacity: isolatedElementId && isolatedElementId !== element.id ? 0.3 : 1
                      }}
                      onClick={() => onSelectElement(element)}
                      onMouseEnter={() => onHoverElement(element.id)}
                      onMouseLeave={() => onHoverElement(null)}
                    >
                      <ElementIcon>{getElementIcon(element.type)}</ElementIcon>
                      <ElementName>
                        {(() => {
                          const { name, label } = getElementDisplayName(element)
                          return (
                            <>
                              {name}
                              {label && (
                                <ElementInfo title={`Type: ${label}`}>
                                  ({label})
                                </ElementInfo>
                              )}
                            </>
                          )
                        })()}
                      </ElementName>
                      {element.locked && (
                        <ElementInfo title="Locked">🔒</ElementInfo>
                      )}
                      {onToggleIsolation && (
                        <VisibilityToggle
                          onClick={(e) => {
                            e.stopPropagation()
                            onToggleIsolation(element.id)
                          }}
                          isVisible={isolatedElementId === element.id}
                          title={isolatedElementId === element.id ? "Exit isolation mode" : "Isolate this element"}
                        >
                          {isolatedElementId === element.id ? '🎯' : '⛶'}
                        </VisibilityToggle>
                      )}
                      {onToggleVisibility && (
                        <VisibilityToggle
                          onClick={(e) => {
                            e.stopPropagation()
                            onToggleVisibility(element.id)
                          }}
                          isVisible={element.visible !== false}
                          title={element.visible !== false ? "Hide element" : "Show element"}
                        >
                          {element.visible !== false ? '👁' : '👁‍🗨'}
                        </VisibilityToggle>
                      )}
                    </ElementItem>
                  ))}
                </>
              )}
            </CategorySection>
          ))
        )}
      </ElementList>
    </PanelContainer>
  )
}

export default NavigatorPanel